/**
 * Componente copiado de ADF 5.0.0 y adaptado a nuestras necesidades
 */

import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { CustomListQueryBuilderService } from '../../../services/custom-list-query-builder.service';
import { SearchFilterService } from '@alfresco/adf-content-services';
import { CustomDataColumn, CustomViewerConfig } from '../../../models/custom-viewer-config';
import { ReloadCustomListTableAction } from '../../../store/actions/alden.actions';
import { AppStore } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { CustomListService } from '../../../services/custom-list.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SMTSearchTextComponent } from './custom-search-widgets/search-text/search-text.component';
import { SMTSearchDateRangeComponent } from './custom-search-widgets/search-date-range/search-date-range.component';
import { SMTSearchListComponent } from './custom-search-widgets/search-list/search-list.component';
import { SMTSearchWidgetChipComponent } from './search-widget-chip/search-widget-chip.component';
import { SMTSearchBooleanComponent } from './custom-search-widgets/search-boolean/search-boolean.component';
import { SMTSearchTextPrequeryComponent } from './custom-search-widgets/search-text-prequery/search-text-prequery.component';
import { ExtensionService } from '@alfresco/adf-extensions';
import { TranslationService } from '@alfresco/adf-core';
import { SMTSearchTextMultiPropertyComponent } from './custom-search-widgets/search-text-multi-property/search-text-multi-propertycomponent';
import { SMTSearchListMultiPropertyComponent } from './custom-search-widgets/search-list-multi-property/search-list-multi-property.component';
import { SMTSearchUserComponent } from './custom-search-widgets/search-user/search-user.component';
import { ResultBuckets } from '@alfresco/js-api';
import { normalizeText } from '../../../utils/string-utils';
import { CustomUserPreferencesService } from '../../../services/custom-user-preferences-service';

@Component({
    selector: 'smt-search-filter',
    templateUrl: './custom-search-filter.component.html',
    styleUrls: ['./custom-search-filter.component.scss']
})
export class CustomSearchFilterComponent implements OnInit, OnDestroy {
    /** Toggles whether to show or not the context facet filters. */
    @Input()
    showContextFacets: boolean = true;

    @Input()
    viewerConfig: CustomViewerConfig;

    @Input()
    defaultFilterValues: { id: string, defaultValue: any }[];

    @ViewChildren(SMTSearchWidgetChipComponent) chips!: QueryList<SMTSearchWidgetChipComponent>;

    //filters: CustomDataColumn[];
    private onDestroy$ = new Subject<boolean>();

    filtersLoaded: boolean;
    auxFilters: CustomDataColumn[] = [];

    firstLoad: boolean = true;
    auxQuerys: number = 0;

    showAllFilters: boolean = false;

    filters: CustomDataColumn[] = [];

    getFilters(): CustomDataColumn[] {
        // 1. Filtrar solo columnas activas para búsqueda
        const activeFilters = this.viewerConfig?.columns
            .map((column, index) => ({ ...column, __index: index })) // guardamos orden original
            .filter(column =>
                column.search.enabled &&
                (!column.visible || column.visible == "true" || (column.visible != "false" && this.extensions.evaluateRule(column.visible)))
            ) ?? [];

        if (activeFilters.length === 0) return activeFilters;

        const sortAlphabetically =
            this.viewerConfig?.sortAlphabeticallyFilters !== undefined &&
                this.viewerConfig?.sortAlphabeticallyFilters !== null
                ? this.viewerConfig.sortAlphabeticallyFilters
                : this.viewerConfig?.sortAlphabetically === true;

        // 2. Detectar si existe al menos un filtro con "order"
        const hasOrders = activeFilters.some(f =>
            f.search?.order !== null && f.search?.order !== undefined
        );

        // 3. Si hay "order", se aplica ordenación especial
        if (hasOrders) {
            const sorted = [...activeFilters];

            sorted.sort((a, b) => {
                const orderA = a.search?.order ?? null;
                const orderB = b.search?.order ?? null;

                /**
                 * Caso A — Ambos sin order (null)
                 * Si sortAlphabetically está activo --> ordenar por título.
                 * Si no --> mantener orden original.
                 */
                if (orderA == null && orderB == null) {
                    if (sortAlphabetically) {
                        const titleA = this.translation.instant(a.search?.title ?? a.title);
                        const titleB = this.translation.instant(b.search?.title ?? b.title);
                        const cmp = titleA.localeCompare(titleB);
                        if (cmp !== 0) return cmp;
                    }
                    return a.__index - b.__index;
                }

                /**
                 * Caso B — Ambos tienen order
                 * Se ordenan por order.
                 * Si el order es igual --> desempate
                 */
                if (orderA != null && orderB != null) {
                    if (orderA !== orderB) return orderA - orderB;

                    if (sortAlphabetically) {
                        const titleA = this.translation.instant(a.search?.title ?? a.title);
                        const titleB = this.translation.instant(b.search?.title ?? b.title);
                        const cmp = titleA.localeCompare(titleB);
                        if (cmp !== 0) return cmp;
                    }

                    return a.__index - b.__index;
                }

                /**
                 * Caso C — Solo uno tiene order
                 * El que tiene order va primero.
                 */
                if (orderA != null) return -1;
                if (orderB != null) return 1;

                return a.__index - b.__index; // fallback final
            });

            return sorted.map(({ __index, ...item }) => item); // limpiamos propiedad interna
        }

        // 4. Si NO hay order pero sí ordenar alfabéticamente
        if (sortAlphabetically) {
            return [...activeFilters]
                .sort((a, b) => {
                    const titleA = this.translation.instant(a.search?.title ?? a.title);
                    const titleB = this.translation.instant(b.search?.title ?? b.title);
                    const cmp = titleA.localeCompare(titleB);
                    if (cmp !== 0) return cmp;

                    return a.__index - b.__index;
                })
                .map(({ __index, ...item }) => item);
        }

        // 5. No hay order y no se ordena alfabéticamente --> devolver en orden original
        return activeFilters
            .sort((a, b) => a.__index - b.__index) // aseguramos orden original SIEMPRE
            .map(({ __index, ...item }) => item);
    }

    get favoriteFilters(): CustomDataColumn[] {
        const favorites = this.filters?.filter(f => f.search.favorite);
        return favorites?.length > 0 ? favorites : this.filters;
    }

    get otherFilters(): CustomDataColumn[] {
        const favorites = this.filters?.filter(f => f.search.favorite);
        return favorites?.length > 0
            ? this.filters.filter(f => !f.search.favorite)
            : [];
    }

    constructor(
        private customListQueryBuilderService: CustomListQueryBuilderService,
        private searchFilterService: SearchFilterService,
        private store: Store<AppStore>,
        private customListService: CustomListService,
        protected extensions: ExtensionService,
        private translation: TranslationService,
        private cdRef: ChangeDetectorRef,
        private customUserPreferencesService:CustomUserPreferencesService
    ) {
        this.searchFilterService.widgets['type-list'] = SMTSearchListComponent;
        this.searchFilterService.widgets['smt-list'] = SMTSearchListComponent;
        this.searchFilterService.widgets['smt-text'] = SMTSearchTextComponent;
        this.searchFilterService.widgets['smt-date-range'] = SMTSearchDateRangeComponent;
        this.searchFilterService.widgets['smt-boolean'] = SMTSearchBooleanComponent;
        this.searchFilterService.widgets['smt-text-prequery'] = SMTSearchTextPrequeryComponent;
        this.searchFilterService.widgets['smt-text-multi-property'] = SMTSearchTextMultiPropertyComponent;
        this.searchFilterService.widgets['smt-list-multi-property'] = SMTSearchListMultiPropertyComponent;
        this.searchFilterService.widgets['smt-user'] = SMTSearchUserComponent;
    }

    ngOnInit(): void {

        this.customUserPreferencesService.selectedSearch$.subscribe(search => {
        if (search) {
            this.loadSearch(search);
        }
        });

        this.filters = this.getFilters();
        if (this.viewerConfig) {
            this.filters.forEach((filter) => {
                if (filter.search.selector?.startsWith("smt-list") || filter.search.selector?.startsWith("type-list")) {
                    //Suscribirse a objeto de DatalistElements
                    this.customListService.dataListsElements.pipe(takeUntil(this.onDestroy$)).subscribe(
                        obj => {
                            if (obj && obj[filter.dataListName as string]) {
                                const objArray = obj[filter.dataListName as string];

                                //Para componente check-list (Se deja por si hace falta)
                                /*objArray.forEach(element => {
                                    this.filterConfig.componentConfig.settings.options.push({name: element.value, value: element.key});
                                })*/

                                filter.search.settings.options = [...objArray];
                                this.refreshFilterChip(filter.search.settings.field);
                                if (this.defaultFilterValues?.length > 0 || filter.search.settings.options) {
                                    this.loadFilter(filter);
                                }
                            }
                        });
                } else {
                    this.loadFilter(filter);
                }
            });
        }

        //Suscribirse al reloadTable (resetFilters)
        this.customListService.reloadTable.pipe(takeUntil(this.onDestroy$)).subscribe((res: { resetFilters: boolean, clearState: boolean}) => {
            if (res?.resetFilters) {
                this.resetFilters(res.clearState);
            }
        });
    }

    loadFilter(filter: CustomDataColumn) {
        if (!this.auxFilters.some((aF) => aF.id === filter.id)) {
            this.auxFilters.push(filter);
        }

        if (this.auxFilters.length == this.filters.length) {
            this.setDefaultValues();
            this.filtersLoaded = true;
            this.cdRef.detectChanges();

            // Cuando no tenga defaultValues
            if (this.firstLoad && (!this.defaultFilterValues || this.defaultFilterValues.length == 0) && this.filters.filter((f) => f.search.settings?.defaultValue !== undefined).length === 0) {
                this.firstLoad = false;
                this.store.dispatch(new ReloadCustomListTableAction(false))
            }
        }
    }

    setDefaultValues() {
        if (this.defaultFilterValues?.length > 0) {
            // Cargar defaultValue
            this.defaultFilterValues.forEach((f) => {
                const filterId = f.id;
                const filter = this.filters.find((filter) => filter.id === filterId);
                if (filter)
                    filter.search.settings.defaultValue = f.defaultValue;
            });
        }
    }

    removeDefaultValues() {
        if (this.defaultFilterValues?.length > 0) {
            // Eliminar defaultValue
            this.defaultFilterValues.forEach((f) => {
                const filterId = f.id;
                const filter = this.filters.find((filter) => filter.id === filterId);
                if (filter)
                    filter.search.settings.defaultValue = undefined;
            });
        }
    }

    ngOnDestroy() {
        this.removeDefaultValues();
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    resetFilters(clearState?:  boolean) {
        this.chips.forEach(chip => {
            if (chip.editable || clearState) {
                chip.reset();
                if(clearState) {
                    this.removeDefaultValues();
                    chip.evaluateEditable();
                }
            }
        });
        this.store.dispatch(new ReloadCustomListTableAction(false));
    }

    refreshFilterChip(filterId: string) {
        this.chips?.forEach(chip => {
            if (chip.filter.search.settings?.field === filterId) {
                chip.refreshFilterChip();
            }
        });
    }

    onFilterChange(event: any) {
        this.customListQueryBuilderService.removeFilterQuery(event.key);
        if (event.value != null && event.value != '') {
            this.customListQueryBuilderService.addFilterQuery({ 'id': event.key, 'query': { query: event.value } });
            if (this.firstLoad)
                this.auxQuerys++;
        }

        if (this.firstLoad && this.auxQuerys === this.filters.filter((f) => f.search.settings.defaultValue !== undefined).length) {
            this.firstLoad = false;
            this.store.dispatch(new ReloadCustomListTableAction(false));
        }
    }

    submitQuery() {
        this.store.dispatch(new ReloadCustomListTableAction(false));
    }

    toggleFilters() {
        this.showAllFilters = !this.showAllFilters;
    }

    refreshFilters(buckets: ResultBuckets[]){
        if(this.viewerConfig.searchConfig.facetFields){
            //Buscar aquellos filterChips que queremos actualizar
            //De momento solamente listas
            this.filters.forEach((filter) => {
                if (filter.search.selector.startsWith("smt-list") || filter.search.selector == "type-list") {

                    //Buscar el bucket correspondiente al filtro
                    const filterId = filter.search.settings.field;
                    const bucket = buckets?.find(bucket => bucket.label  === filterId);

                    //Obtener dataListsElements
                    const dataListsElements = this.customListService.dataListsElements.getValue();
                    if(dataListsElements && dataListsElements[filter.dataListName as string]) {
                        let elementsArray = dataListsElements[filter.dataListName as string];

                        //filtrar solamente los del bucket y añadir número
                        if(bucket){
                            elementsArray = elementsArray.filter(item =>
                                    bucket?.buckets?.some(bucketItem => normalizeText(bucketItem.label) === normalizeText(item.key))
                                ).map(item => {
                                    const bucketItem = bucket?.buckets?.find(bucketItem => normalizeText(bucketItem.label) === normalizeText(item.key));

                                    return {
                                        ...item,
                                        value: `${item.value} (${bucketItem!.count})`
                                    };
                                });

                            //Actualizar en el filtro
                            filter.search.settings.options = [...elementsArray];
                        }else if(!bucket && this.viewerConfig.searchConfig.facetFields?.facets?.some(facet => facet.field === filterId)){
                            //Si no tiene bucket pero si es un filtro con facet se deja sin elementos, ya que quiere decir que ningun nodo tiene ese dato
                            //Actualizar en el filtro
                            filter.search.settings.options = [];
                        }
                        //Refrescar
                        this.refreshFilterChip(filterId);
                    }
                }
            });
        }
    }


  loadSearch(search:any){
    this.chips.filter(chip => chip.hasValidValue() || search[chip.filter.id!]).forEach(chip => {
        const value = search[chip.filter.id!] ?? undefined;
        chip.setSavedValue(value);
    });
  }
}
